import { Component, OnDestroy, OnInit } from '@angular/core';
import { MatSlideToggleChange } from '@angular/material/slide-toggle';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { FeatureFlagService } from '@vendasta/businesses';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import { LegacyStatus } from '@vendasta/notifications-sdk';
import { Observable, combineLatest, of } from 'rxjs';
import { filter, map, switchMap, tap } from 'rxjs/operators';
import { AppConfigService } from '../../app-config.service';
import { VaDialogService } from '../../common/va-dialog.service';
import { NotificationChange } from './notification-change';
import { TabChange, UserBulkActionsService } from './user-bulk-actions.service';

export const enum SELECT_OPTION {
  DONT_UPDATE = 0,
  GIVE_ACCESS,
  REMOVE_ACCESS,
}

@Component({
  selector: 'app-user-bulk-actions',
  templateUrl: './user-bulk-actions.component.html',
  styleUrls: ['./user-bulk-actions.component.scss'],
})
export class UserBulkActionsComponent implements OnInit, OnDestroy {
  public readonly LegacyStatus = LegacyStatus;
  applyChangesBtnText$: Observable<string>;

  noChangesSelected = true;
  notifChanges = false;
  tabChanges = false;

  tabAccessChanges$: Observable<[TabChange[], TabChange[]]>;
  notificationChanges$: Observable<NotificationChange[]>;

  showDefaultNotificationPromotes = true;
  newDefaultNotificationEnabled$: Observable<boolean>;
  private dialogListenerSub: any;

  constructor(
    private userBulkActionsService: UserBulkActionsService,
    private router: Router,
    private snackbarService: SnackbarService,
    private dialogService: VaDialogService,
    private translateService: TranslateService,
    private featureFlagService: FeatureFlagService,
    private configService: AppConfigService,
  ) {}

  ngOnInit(): void {
    // If no users are selected, they must have directly gone to this page -
    // redirect them back to the users select screen so they can select users
    if (!this.userBulkActionsService.areUsersSelected()) {
      this.router.navigate(['/bc-admin']);
      return;
    }

    this.userBulkActionsService.resetSelections();

    this.applyChangesBtnText$ = combineLatest([
      this.translateService.stream('USERS.BULK_UPDATE_SETTINGS.APPLY_CHANGES_TO_NAME'),
      this.userBulkActionsService.getAmountOfUsersSelectedStringAsync(),
    ]).pipe(
      map(([applyChangesString, numUsersString]) => {
        return applyChangesString.replace('{{name}}', numUsersString);
      }),
    );

    this.notificationChanges$ = this.userBulkActionsService.notificationSettings$.pipe(
      tap((notifs) => {
        if (notifs.length > 0) {
          this.notifChanges = true;
          this.noChangesSelected = false;
        } else {
          this.notifChanges = false;
          if (!this.tabChanges) {
            this.noChangesSelected = true;
          }
        }
      }),
    );
    this.tabAccessChanges$ = combineLatest([
      this.userBulkActionsService.tabsGiveAccess$,
      this.userBulkActionsService.tabsRemoveAccess$,
    ]).pipe(
      tap(([give, remove]) => {
        if (give.length > 0 || remove.length > 0) {
          this.tabChanges = true;
          this.noChangesSelected = false;
        } else {
          this.tabChanges = false;
          if (!this.notifChanges) {
            this.noChangesSelected = true;
          }
        }
      }),
    );

    this.newDefaultNotificationEnabled$ = this.configService.config$.pipe(
      switchMap((config) =>
        this.featureFlagService.checkFeatureFlag(config.partnerId, '', 'new_notification_default_settings'),
      ),
    );
  }

  ngOnDestroy(): void {
    if (this.dialogListenerSub) {
      this.dialogListenerSub.unsubscribe();
    }
  }

  applyChanges(): void {
    if (this.noChangesSelected) {
      this.snackbarService.errorSnack(this.translateService.instant('USERS.BULK_UPDATE_SETTINGS.MUST_SELECT_CHANGES'));
      return;
    }
    this.dialogListenerSub = combineLatest([
      this.translateService.stream('USERS.BULK_UPDATE_SETTINGS.DIALOG.OVERWRITE_PREVIOUS_CHANGES'),
      this.getChangesTextAsync(),
      this.userBulkActionsService.getAmountOfUsersSelectedStringAsync(),
    ])
      .pipe(
        switchMap(([previousChangesText, changesText, numUsersText]) => {
          return this.dialogService.confirm(
            'USERS.BULK_UPDATE_SETTINGS.DIALOG.UPDATE_SETTINGS',
            previousChangesText.replace('{{changesText}}', changesText).replace('{{name}}', numUsersText),
            'USERS.BULK_UPDATE_SETTINGS.DIALOG.YES_UPDATE_SETTINGS',
            'USERS.BULK_UPDATE_SETTINGS.DIALOG.NO_DONT_UPDATE',
          );
        }),
        filter((response) => response === 'yes'),
        switchMap((_) => this.userBulkActionsService.submitChanges()),
      )
      .subscribe({
        next: () => {
          this.snackbarService.successSnack(this.translateService.instant('USERS.BULK_UPDATE_SETTINGS.SUCCESS'));
          this.router.navigate(['/bc-admin']);
        },
        error: () => {
          this.snackbarService.errorSnack(this.translateService.instant('USERS.BULK_UPDATE_SETTINGS.ERROR'));
        },
      });
  }

  updateBulkNotificationScope(event: unknown): void {
    this.userBulkActionsService.setBulkUpdateScope((event as MatSlideToggleChange).checked);
  }

  private getChangesTextAsync(): Observable<string> {
    if (this.notifChanges && this.tabChanges) {
      return this.translateService.stream('USERS.BULK_UPDATE_SETTINGS.TAB_ACCESS_AND_NOTIFICATIONS');
    }
    if (this.tabChanges) {
      return this.translateService.stream('USERS.BULK_UPDATE_SETTINGS.TAB_ACCESS_LOWER');
    }
    if (this.notifChanges) {
      return this.translateService.stream('USERS.BULK_UPDATE_SETTINGS.PERMISSIONS');
    }
    return of('');
  }

  public defaultNotificationPromoteClose(): void {
    this.showDefaultNotificationPromotes = false;
  }
}
